import { Link, LinkProps } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';

const wine = '#800020';
const wineLight = '#A00030';
const white = '#FFFFFF';
const lightGray = '#D3D3D3';
const black = '#000000';

const slideUp = keyframes`
  0% {
    opacity: 0;
    transform: translateY(10px);
  }

  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

type ContainerParams = {
  open?: boolean;
};

export const Container = styled.aside<ContainerParams>`
  position: relative;
  display: grid;
  grid-template-rows: 1fr auto;
  grid-template-columns: 1fr;
  padding: 1.8rem 1rem;
  background-color: ${lightGray};
  z-index: 100;

  @media screen and (max-width: 767px) {
    position: absolute;
    top: 0;
    left: ${(props) => (props.open ? '0%' : '-100%')};
    width: 80vw;
    height: 100vh;
    transition: left 0.2s;

    ${(props) => props.open && 'left: 0%'};
  }
`;

export const Menu = styled.nav`
  display: grid;
  grid-template-rows: min-content;
  grid-template-columns: 1fr;
  grid-auto-rows: min-content;
  row-gap: 0.6rem;
  overflow-y: scroll;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 17px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background-color: ${wine};
  }

  &::-webkit-scrollbar-track {
    background-color: ${lightGray};
  }
`;

type ActiveParams = {
  active?: boolean;
  dropdownOpen?: boolean;
};

const itemsColorsScheme = css<ActiveParams>`
  background-color: ${({ active }) => (active ? white : 'transparent')};
  color: ${({ active }) => (active ? wine : black)};

  &:hover {
    background-color: ${({ active }) => (active ? white : wineLight)};
    color: ${({ active }) => (active ? wine : white)};
    svg {
      color: ${({ active }) => (active ? wine : white)};
    }
  }

  svg {
    color: ${({ active }) => (active ? wine : black)};
  }
`;

export const MenuLink = styled(Link)<ActiveParams>`
  ${itemsColorsScheme}
  display: grid;
  grid-template-rows: min-content;
  grid-template-columns: min-content 1fr;
  column-gap: 15px;
  justify-items: left;
  align-items: center;
  padding: 0.6rem;
  text-align: left;
  font-family: Arial, sans-serif;
  font-size: 16px;
  font-weight: ${({ active }) => (active ? 700 : 400)};
  border-radius: 4px;
  transition: background-color 0.2s ease-in, color 0.2s ease-in;
`;

export const DropdownButton = styled.button<ActiveParams>`
  ${itemsColorsScheme}
  width: 100%;
  display: grid;
  grid-template-rows: min-content;
  grid-template-columns: min-content 1fr min-content;
  column-gap: 15px;
  justify-items: left;
  align-items: center;
  padding: 0.6rem;
  text-align: left;
  font-family: Arial, sans-serif;
  font-size: 16px;
  font-weight: ${({ active }) => (active ? 700 : 400)};
  border-radius: 4px;
  transition: background-color 0.2s ease-in, color 0.2s ease-in;

  > div:last-child > svg {
    transition: 0.1s ease-out;
    transform: ${({ dropdownOpen }) =>
      dropdownOpen ? 'rotate(90deg)' : 'rotate(0)'};
  }
`;

export const DropdownMenu = styled.ul<ActiveParams>`
  display: ${({ active }) => (active ? 'grid' : 'none')};
  grid-template-rows: min-content;
  grid-template-columns: 1fr;
  grid-auto-rows: min-content;
  row-gap: 0.1rem;
  padding: 0;
  margin: 0.3rem 0 0;
  list-style: none;
  animation: ${slideUp} 0.2s ease-in-out;
`;

type DropdownLinkParams = LinkProps & {
  active?: boolean;
};

export const DropdownLink = styled(Link)<DropdownLinkParams>`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-left: 2px;
  padding: 0.6rem;
  text-decoration: none;
  color: ${(props) => (props.active ? wine : black)};
  font-size: 14px;
  font-weight: 700;
  border-radius: 4px;
  transition: margin-left 0.2s ease-in-out, background-color 0.2s ease-in,
    color 0.2s ease-in-out;

  &:hover {
    margin-left: 4px;
    color: ${white};
    background-color: ${wineLight};
  }
`;

export const CopyrightContainer = styled.footer`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  text-align: center;
  color: ${black};
  font-size: 12px;

  img {
    width: 120px;
    @media screen and (min-width: 768px) {
      max-width: 60%;
    }
  }
`;

export const Version = styled.section`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 15px;
  text-align: center;
  font-size: 15px;
  color: ${({ theme }) => theme.colors.black};
`;